import { Box, Flex, Text } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { customPaddingStyles } from 'containers/Integrations/shared/react-select-custom';
import {
  useFetchPaginatedGustoEmployees,
  IGustoEmployeeSearchQuery,
} from 'hooks/integrations/useFetchPaginatedGustoEmployee';
import { fetchPaginatedGustoTimeeroEmployees } from 'hooks/integrations/useFetchPaginatedGustoTimeeroEmployee';
import { useImportGustoEmployees, IGustoEmployees } from 'hooks/integrations/useImportGustoEmployees';
import {
  useMapGustoTimeeroEmployeeAndJob,
  IGustoTimeeroEmployeeAndJob,
} from 'hooks/integrations/useMapGustoTimeeroEmployeeAndJob';
import { useSyncGusto } from 'hooks/integrations/useSyncGusto';
import { useUnmapGustoTimeeroEmployee, IGustoUnmapTimeeroEmployee } from 'hooks/integrations/useUnMapGustoEmployee';
import { getAsyncSelectLoadOptions } from 'utils/helpers';
import toaster from 'utils/toaster';

import GustoMappingTableFooter from './GustoMappingTableFooter';

const initialSearchQuery: IGustoEmployeeSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const GustoMapping = () => {
  const [mappedUsers, setMappedUsers] = useState<any>(null);
  const [currentMapUser, setCurrentMapUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IGustoEmployeeSearchQuery>(initialSearchQuery);

  const { t } = useTranslation(['common', 'integrations', 'jobsTasks']);

  const {
    data: gustoEmployeeData,
    status: gustoEmployeeStatus,
    refetch,
  } = useFetchPaginatedGustoEmployees(searchQuery);
  const { status: gustoEmployeeAndJobMapStatus, mutateAsync: gustoEmployeeAndJobMapMutateAsync } =
    useMapGustoTimeeroEmployeeAndJob();
  const { status: gustoUnmapEmployeeStatus, mutateAsync: gustoUnmapEmployeeMutateAsync } =
    useUnmapGustoTimeeroEmployee();
  const { status: gustoImportEmployeesStatus, mutateAsync: gustoImportEmployeesMutateAsync } =
    useImportGustoEmployees();
  const { status: gustoSyncStatus, mutateAsync: gustoSyncMutateAsync } = useSyncGusto();

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
      },
    },
    {
      Header: t('integrations:gusto.gustoUsers'),
      accessor: 'name',
      width: 100,
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.name}</Text>;
      },
    },
    {
      Header: t('integrations:timeeroUsers'),
      accessor: 'list',
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={timeeroEmployeeLoadOptions}
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.selected_user
                ? data.row.original?.selected_user || null
                : mappedUsers &&
                    mappedUsers[`${data.row.original.id}`] &&
                    mappedUsers[`${data.row.original.id}`].employee
                  ? mappedUsers[`${data.row.original.id}`].employee
                  : null
            }
            onChange={(value: any) => {
              setMappedUsers((prevValue: any) => ({
                ...prevValue,
                [data.row.original.id]: {
                  employee: value || null,
                },
              }));
            }}
            size="md"
            variant="filled"
            padding="14px 8px"
            fontSize="sm"
            debounceTimeout={300}
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('integrations:selectEmployees')}
            formatOptionLabel={formatOptionLabel}
            getOptionValue={(option: any) => option.userId}
            styles={customPaddingStyles}
          />
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 80,
      Cell: (data: CellValue) => {
        return data.row.original.selected_user && data.row.original.selected_user ? (
          <Button
            _hover={{ bg: 'secondary.100' }}
            fontWeight="bold"
            background="rgba(250, 85, 85, 0.2)"
            color="rgba(250, 85, 85, 1)"
            isLoading={gustoUnmapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id}
            onClick={() => onUnMapEmployees(data.row.original.id)}
          >
            {t('integrations:removeMapping')}
          </Button>
        ) : (
          <Button
            _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
            background="rgba(255, 103, 32, 0.1)"
            colorScheme="rgba(255, 103, 32, 0.3)"
            color="secondary.500"
            isLoading={gustoEmployeeAndJobMapStatus === 'loading' && currentMapUser === data.row.original.id}
            onClick={() => onMapEmployees(data.row.original.id)}
          >
            {t('integrations:mapEmployee')}
          </Button>
        );
      },
    },
  ];

  const formatOptionLabel = (option: any) => {
    return (
      <Box>
        <Text fontSize="sm" fontWeight={600}>
          {option.firstName + ' ' + option.lastName}
        </Text>
        <Text fontSize="sm" fontWeight={400}>
          {option.email}
        </Text>
      </Box>
    );
  };

  const onMapEmployees = (gustoId: number) => {
    if (mappedUsers && mappedUsers[gustoId] && mappedUsers[gustoId].hasOwnProperty('employee')) {
      setCurrentMapUser(gustoId);
      const postData: IGustoTimeeroEmployeeAndJob = {
        gusto_employee_id: gustoId,
        user_id: parseInt(mappedUsers[gustoId].employee.id),
      };
      gustoEmployeeAndJobMapMutateAsync(postData).then(() => {
        refetch();
      });
    } else {
      toaster.error({ description: t('integrations:employeeSelectMapError') });
    }
  };

  const onUnMapEmployees = (gustoId: number) => {
    setCurrentMapUser(gustoId);
    const postData: IGustoUnmapTimeeroEmployee = {
      gusto_employee_id: gustoId,
    };
    gustoUnmapEmployeeMutateAsync(postData).then(() => {
      setCurrentMapUser(null);
      setMappedUsers((prevValue: any) => ({
        ...prevValue,
        [gustoId]: null,
      }));
      refetch();
    });
  };

  const onImportJobs = (selectedIds: Array<number>) => {
    const postData: IGustoEmployees = {
      gusto_employee_ids: selectedIds,
    };
    gustoImportEmployeesMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const onSyncGusto = () => {
    gustoSyncMutateAsync().then(() => {
      refetch();
    });
  };

  const timeeroEmployeeLoadOptions = async (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedGustoTimeeroEmployees, { search, page });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IGustoEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    const updatedSearchQuery = (previousSearchQuery: IGustoEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page_size: pageSize,
      page: 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <Box>
      <Title>{t('integrations:gusto.gustoMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={gustoSyncStatus === 'loading'}
          onClick={onSyncGusto}
        >
          {t('integrations:gusto.syncChanges')}
        </Button>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: t('common:searchUsers'),
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <Table
        columns={columns}
        data={gustoEmployeeData?.payload || []}
        isLoading={gustoEmployeeStatus === 'loading'}
        enableRowSelect
        paginationData={gustoEmployeeData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <GustoMappingTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleImportJobs={onImportJobs}
              isLoading={gustoImportEmployeesStatus === 'loading'}
              t={t}
            />
          );
        }}
      />
    </Box>
  );
};
export default GustoMapping;
